import { Button } from '@/components/ui/button';
import { Printer } from 'lucide-react';
import { ScryfallCard } from '@/types/scryfall';

interface PrintDecklistButtonProps {
  cards: (ScryfallCard & { count: number })[];
  title: string;
  className?: string;
}

export function PrintDecklistButton({ cards, title, className }: PrintDecklistButtonProps) {
  const handlePrint = () => {
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow popups to print the decklist');
      return;
    }

    // Filter out cards without images
    const printableCards = cards.filter(card => card.image_uris?.normal || card.image_uris?.small);
    
    if (printableCards.length === 0) {
      alert('No cards with images found to print');
      printWindow.close();
      return;
    }

    // Generate the print HTML
    const printHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Print ${title.replace(/"/g, '&quot;')}</title>
          <meta charset="utf-8">
          <style>
            @page {
              margin: 0;
              size: auto;
            }
            
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              font-family: system-ui, -apple-system, sans-serif;
              background: white;
              padding: 0;
              margin: 0;
            }
            
            .print-container {
              display: grid;
              grid-template-columns: repeat(auto-fit, 2.5in);
              gap: 0;
              padding: 0;
              margin: 0;
              width: 100%;
            }
            
            .card-wrapper {
              width: 2.5in;
              height: 3.5in;
              padding: 0;
              margin: 0;
              page-break-inside: avoid;
              break-inside: avoid;
              position: relative;
              overflow: hidden;
              background: #f0f0f0;
            }
            
            .card-image {
              width: 100%;
              height: 100%;
              object-fit: cover;
              display: block;
            }
            
            .card-count {
              position: absolute;
              top: 4px;
              left: 4px;
              background: rgba(0, 0, 0, 0.8);
              color: white;
              padding: 2px 6px;
              border-radius: 4px;
              font-size: 12px;
              font-weight: bold;
              z-index: 1;
            }
            
            @media print {
              body {
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
              }
              
              .print-container {
                grid-template-columns: repeat(auto-fit, 2.5in);
              }
            }
          </style>
        </head>
        <body>
          <div class="print-container">
            ${printableCards.map(card => {
              // Repeat each card based on its count
              return Array.from({ length: card.count }, (_, index) => `
                <div class="card-wrapper">
                  <img 
                    src="${(card.image_uris?.normal || card.image_uris?.small || '').replace(/"/g, '&quot;')}" 
                    alt="${card.name.replace(/"/g, '&quot;')}"
                    class="card-image"
                    onload="this.previousElementSibling.style.display='none'"
                    onerror="this.previousElementSibling.textContent='Image failed to load'"
                  />
                  ${card.count > 1 && index === 0 ? `<div class="card-count">${card.count}</div>` : ''}
                </div>
              `).join('');
            }).join('')}
          </div>
          
          <script>
            // Wait for all images to load before printing
            let loadedImages = 0;
            const totalImages = ${printableCards.reduce((sum, card) => sum + card.count, 0)};
            const images = document.querySelectorAll('.card-image');
            
            function checkAllLoaded() {
              loadedImages++;
              if (loadedImages >= totalImages) {
                setTimeout(() => {
                  window.print();
                  window.close();
                }, 500);
              }
            }
            
            images.forEach(img => {
              if (img.complete) {
                checkAllLoaded();
              } else {
                img.addEventListener('load', checkAllLoaded);
                img.addEventListener('error', checkAllLoaded);
              }
            });
            
            // Fallback timeout
            setTimeout(() => {
              window.print();
              window.close();
            }, 10000);
          </script>
        </body>
      </html>
    `;

    // Write the HTML to the new window
    printWindow.document.write(printHTML);
    printWindow.document.close();
  };

  return (
    <Button onClick={handlePrint} variant="outline" size="sm" className={className}>
      <Printer className="h-4 w-4 sm:mr-2" />
      <span className="hidden sm:inline">Print</span>
    </Button>
  );
}